import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { LogIn, AlertCircle } from 'lucide-react';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/login.css';

/**
 * Login Page Component
 * Handles user authentication
 */
const LoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login, loading, error: contextError } = useAuth();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Validation
    if (!username.trim()) {
      setError(t('form.validation.username_required'));
      setIsLoading(false);
      return;
    }

    if (!password) {
      setError(t('form.validation.password_required'));
      setIsLoading(false);
      return;
    }

    try {
      await login(username, password);
      navigate('/');
    } catch (err) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        {/* Logo/Header */}
        <div className="login-header">
          <LogIn size={40} className="login-icon" />
          <h1 className="login-title">{t('login.appTitle')}</h1>
          <p className="login-subtitle">{t('login.subtitle')}</p>
        </div>

        {/* Error Messages */}
        {(error || contextError) && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            <AlertCircle size={20} className="me-2" />
            <strong>{t('login.errorLabel')}</strong> {error || contextError}
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmit}>
          {/* Username Field */}
          <div className="mb-3">
            <label htmlFor="username" className="form-label">
              {t('login.usernameLabel')}
            </label>
            <input
              type="text"
              className="form-control form-control-lg"
              id="username"
              placeholder={t('login.usernamePlaceholder')}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={isLoading || loading}
              required
            />
          </div>

          {/* Password Field */}
          <div className="mb-4">
            <label htmlFor="password" className="form-label">
              {t('password')}
            </label>
            <input
              type="password"
              className="form-control form-control-lg"
              id="password"
              placeholder={t('login.passwordPlaceholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading || loading}
              required
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="btn btn-primary btn-lg w-100 mb-3"
            disabled={isLoading || loading}
          >
            {isLoading || loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                {t('login.signingIn')}
              </>
            ) : (
              t('login.signIn')
            )}
          </button>
        </form>

        {/* Demo Credentials Info */}
		{window.location.hostname === 'localhost' && (
		  <div className="demo-info alert alert-info" role="alert">
		    <strong>{t('login.demoCredentials')}</strong>
		    <br />
		    {t('login.demoUsername')}
		    <br />
		    {t('login.demoPassword')}
		  </div>
		)}

        {/* Footer */}
        <div className="login-footer">
          <p className="text-muted">
            {t('login.copyright')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
